// --- NEW FILE: packages/agent-kit/src/hooks/projects/useProjectVersions.ts ---

import { useState, useCallback } from 'react';
import useSWR from 'swr';
import { adminProjectService, isApiError } from '@dopomogai/api-client';
import type { Project, ProjectVersion, UUID, RevertPayload } from '@dopomogai/types';
import { AgentKitError } from '../../types';

/**
 * A hook for fetching the version history of a Project.
 * @param projectId The UUID of the project. If undefined, no fetch occurs.
 */
export const useProjectVersionHistory = (projectId?: UUID) => {
  const swrKey = projectId ? ['/admin/project/versions', projectId] : null;

  const { data, error, isLoading, mutate } = useSWR<ProjectVersion[], AgentKitError>(
    swrKey,
    async ([, projId]: [string, UUID]) => {
      const response = await adminProjectService.listProjectVersions(projId);
      if (isApiError(response)) {
        throw new AgentKitError(`Failed to fetch project versions for ${projId}`, response);
      }
      return response;
    }
  );

  return { 
    versions: data, 
    error, 
    isLoading, 
    revalidate: mutate 
  };
};

/**
 * A hook that provides the function to revert a Project to a previous version.
 * This is a "mutation" hook and does not fetch data itself.
 */
export const useRevertProject = () => {
    const [isReverting, setIsReverting] = useState(false);
    const [error, setError] = useState<AgentKitError | null>(null);

    /**
     * Triggers the revert action for a project.
     * @param projectId The UUID of the project to revert.
     * @param payload The revert payload containing the target version and a commit message.
     * @returns The reverted Project object on success, or null on failure.
     */
    const revertProject = useCallback(async (projectId: UUID, payload: RevertPayload): Promise<Project | null> => {
        setIsReverting(true);
        setError(null);
        const res = await adminProjectService.revertProject(projectId, payload);
        if (isApiError(res)) {
            setError(new AgentKitError('Failed to revert project', res));
            setIsReverting(false);
            return null;
        }
        setIsReverting(false);
        return res;
    }, []);
    
    return { 
      revertProject, 
      isReverting, 
      error 
    };
};